import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg, track, useSeo } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Summary, toPayload } from "./Cart";
import { PaymentBrick } from "@/components/store/OrderParts";

const STEPS = ["Identificação", "Endereço", "Entrega", "Pagamento", "Confirmação"];
const F = ({ label, k, obj, set, ...rest }) => (
  <div><label className="label" htmlFor={`f-${k}`}>{label}</label><input id={`f-${k}`} className="input" value={obj[k] || ""} onChange={(e) => set({ ...obj, [k]: e.target.value })} data-testid={`checkout-${k}`} {...rest} /></div>
);

export default function Checkout() {
  const { cart, customer, clearCart, cartId } = useStore();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [c, setC] = useState({});
  const [a, setA] = useState({ cep: localStorage.getItem("e3_cep") || "" });
  const [methods, setMethods] = useState([]);
  const [ship, setShip] = useState("");
  const [pr, setPr] = useState(null);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idem] = useState(() => crypto.randomUUID());
  useSeo({ title: "Checkout" });

  useEffect(() => { if (customer) { setC((x) => ({ first_name: customer.first_name, last_name: customer.last_name, email: customer.email, cpf: customer.cpf, phone: customer.phone, ...x })); const last = customer.addresses?.slice(-1)[0]; if (last) setA((x) => ({ ...last, ...x, cep: x.cep || last.cep })); } }, [customer]);
  useEffect(() => { if (!cart.length && !order) nav("/carrinho"); else track("BeginCheckout", { num_items: cart.length }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const coupon = localStorage.getItem("e3_coupon") || null;
  const reprice = async (code = ship) => {
    try { setPr((await api.post("/cart/price", { items: toPayload(cart), coupon, email: c.email, cep: a.cep, shipping_code: code || null })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { reprice(); }, [ship]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookupCep = async (cep) => {
    if (cep.replace(/\D/g, "").length !== 8) return;
    try { const r = (await api.get(`/cep/${cep.replace(/\D/g, "")}`)).data; setA((x) => ({ ...x, street: r.logradouro || x.street, district: r.bairro || x.district, city: r.localidade || x.city, state: r.uf || x.state })); } catch (e) { toast.error("CEP não encontrado"); }
  };
  const next = async () => {
    if (step === 0) {
      if (["first_name", "last_name", "cpf", "email", "phone"].some((k) => !c[k])) return toast.error("Preencha todos os campos");
      if (c.cpf.replace(/\D/g, "").length !== 11) return toast.error("CPF inválido");
      localStorage.setItem("e3_email", c.email);
      api.post("/cart/sync", { cart_id: cartId(), email: c.email, name: c.first_name, phone: c.phone, items: cart.map((i) => ({ name: i.name, qty: i.qty, price: i.unit_price })) }).catch(() => {});
    }
    if (step === 1) {
      if (["cep", "street", "number", "district", "city", "state"].some((k) => !a[k])) return toast.error("Preencha o endereço completo");
      try { const q = (await api.post("/shipping/quote", { cep: a.cep, items: toPayload(cart) })).data; setMethods(q.methods); setShip(q.methods[0]?.code || ""); } catch (e) { return toast.error(errMsg(e)); }
    }
    if (step === 2) {
      if (!ship) return toast.error("Escolha a entrega");
      setBusy(true);
      try {
        const r = (await api.post("/checkout/order", { items: toPayload(cart), customer: c, address: a, shipping_code: ship, coupon, idempotency_key: idem, cart_id: cartId(), notes: c.notes || "" })).data;
        setOrder({ ...r, email: c.email });
      } catch (e) { setBusy(false); return toast.error(errMsg(e)); }
      setBusy(false);
    }
    setStep(step + 1);
  };
  const onPaid = (o) => {
    clearCart();
    localStorage.removeItem("e3_coupon");
    if (o.payment_status === "aprovado") track("Purchase", { value: o.total, currency: "BRL", transaction_id: o.number });
    nav(`/pedido/sucesso?id=${o.id}&token=${order.token}`);
  };
  const finishWithoutPayment = () => { clearCart(); nav(`/pedido/sucesso?id=${order.id}&token=${order.token}`); };

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-10">
      <ol className="flex items-center gap-2 mb-10 overflow-x-auto scrollbar-none" data-testid="checkout-steps">
        {STEPS.map((s, k) => (
          <li key={s} className="flex items-center gap-2 shrink-0" data-testid={`checkout-step-${k + 1}`}>
            <span className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold ${k < step ? "bg-[#008CFF]" : k === step ? "border-2 border-[#00C8FF] text-[#00C8FF]" : "border border-[#282D37] text-[#6b7079]"}`}>{k < step ? <Check className="h-4 w-4" /> : k + 1}</span>
            <span className={`text-sm font-semibold ${k === step ? "" : "text-[#6b7079] hidden sm:inline"}`}>{s}</span>
            {k < STEPS.length - 1 && <span className="w-6 h-px bg-[#282D37] hidden sm:block" />}
          </li>
        ))}
      </ol>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="card p-6 lg:p-8">
          {step === 0 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center"><h2 className="text-xl font-semibold">Identificação</h2>{!customer && <Link to="/minha-conta?next=/checkout" className="text-sm text-[#00C8FF]" data-testid="checkout-login-link">Já tenho conta</Link>}</div>
              <p className="text-sm text-[#A0A0A0]">{customer ? `Comprando como ${customer.email}` : "Compre como visitante ou entre na sua conta."}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Nome" k="first_name" obj={c} set={setC} autoComplete="given-name" /><F label="Sobrenome" k="last_name" obj={c} set={setC} autoComplete="family-name" />
                <F label="CPF" k="cpf" obj={c} set={setC} inputMode="numeric" placeholder="000.000.000-00" /><F label="Telefone / WhatsApp" k="phone" obj={c} set={setC} inputMode="tel" autoComplete="tel" />
                <div className="sm:col-span-2"><F label="E-mail" k="email" obj={c} set={setC} type="email" autoComplete="email" /></div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Endereço de entrega</h2>
              <div className="grid sm:grid-cols-6 gap-4">
                <div className="sm:col-span-2"><label className="label">CEP</label><input className="input" inputMode="numeric" value={a.cep || ""} onChange={(e) => { setA({ ...a, cep: e.target.value }); lookupCep(e.target.value); }} data-testid="checkout-cep" /></div>
                <div className="sm:col-span-4"><F label="Rua" k="street" obj={a} set={setA} /></div>
                <div className="sm:col-span-2"><F label="Número" k="number" obj={a} set={setA} /></div>
                <div className="sm:col-span-4"><F label="Complemento" k="complement" obj={a} set={setA} /></div>
                <div className="sm:col-span-2"><F label="Bairro" k="district" obj={a} set={setA} /></div>
                <div className="sm:col-span-3"><F label="Cidade" k="city" obj={a} set={setA} /></div>
                <div className="sm:col-span-1"><F label="UF" k="state" obj={a} set={setA} maxLength={2} /></div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold mb-2">Entrega</h2>
              {methods.map((m) => (
                <label key={m.code} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer ${ship === m.code ? "border-[#008CFF] bg-[#008CFF]/5" : "border-[#282D37]"}`} data-testid={`ship-option-${m.code}`}>
                  <input type="radio" name="ship" className="accent-[#008CFF]" checked={ship === m.code} onChange={() => setShip(m.code)} />
                  <div className="flex-1"><div className="font-semibold">{m.name}</div><div className="text-xs text-[#A0A0A0]">Produção: {m.production_days} dias úteis + Transporte: {m.transport_days} dias úteis · Estimativa: até {m.total_days} dias úteis</div></div>
                  <b>{m.price ? brl(m.price) : "Grátis"}</b>
                </label>
              ))}
              <div><label className="label">Observações do pedido</label><textarea className="input" value={c.notes || ""} onChange={(e) => setC({ ...c, notes: e.target.value })} data-testid="checkout-notes" /></div>
            </div>
          )}
          {step === 3 && order && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold">Pagamento · Pedido {order.number}</h2>
              <p className="text-xs text-[#A0A0A0]">Pagamento processado com segurança pelo Mercado Pago. Os dados do cartão não passam pelos nossos servidores. Pix com desconto aplicado na confirmação.</p>
              <PaymentBrick order={order} onPaid={onPaid} />
              <button className="btn-ghost w-full" onClick={finishWithoutPayment} data-testid="go-order-status">Ver status do pedido</button>
            </div>
          )}
          {step < 3 && (
            <div className="flex justify-between mt-8">
              <button className="btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="checkout-back">Voltar</button>
              <button className="btn-primary px-8" disabled={busy} onClick={next} data-testid="checkout-next">{step === 2 ? (busy ? "Criando pedido..." : "Ir para pagamento") : "Continuar"}</button>
            </div>
          )}
        </div>
        <aside className="card p-6 h-fit lg:sticky lg:top-24">
          <h3 className="font-semibold mb-4">Resumo</h3>
          <div className="space-y-2 mb-4">{cart.map((i) => <div key={i.key} className="flex justify-between text-sm gap-2"><span className="truncate">{i.qty}× {i.name}</span><span>{brl(i.unit_price * i.qty)}</span></div>)}</div>
          <Summary pr={order ? { ...pr, total: order.total } : pr} />
        </aside>
      </div>
    </div>
  );
}
